// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { StringUtils } from '@shared/utils/StringUtils';
import { DataCache } from '../DataCache';
import { BaseSettingsUtils } from '../UtilsClasses/BaseGeneralSettingsUtilsWrapper';
import { PositionInfoType } from './PositionInfoType';
var PositionInfoBackColorNames;
(function (PositionInfoBackColorNames) {
    PositionInfoBackColorNames["None"] = "position-info-item-none";
    PositionInfoBackColorNames["PLByCurrencyPositive"] = "pl-by-currency-positive";
    PositionInfoBackColorNames["PLByCurrencyNegative"] = "pl-by-currency-negative";
    PositionInfoBackColorNames["PLByCurrencyNone"] = "pl-by-currency-none";
    PositionInfoBackColorNames["BreakEvenLongShortBoth"] = "breakeven-longshort-both";
    PositionInfoBackColorNames["BreakEvenShortPositive"] = "breakeven-short-positive";
    PositionInfoBackColorNames["BreakEvenLongPositive"] = "breakeven-long-positive";
    PositionInfoBackColorNames["BreakEvenNone"] = "breakeven-none";
    PositionInfoBackColorNames["NetQTYPositive"] = "netqty-positive";
    PositionInfoBackColorNames["NetQTYNegative"] = "netqty-negative";
    PositionInfoBackColorNames["NetQTYNone"] = "netqty-none";
    PositionInfoBackColorNames["AverageOpenPriceLong"] = "average-open-price-long";
    PositionInfoBackColorNames["AverageOpenPriceShort"] = "average-open-price-short";
    PositionInfoBackColorNames["AverageOpenPriceNone"] = "average-open-price-none";
    PositionInfoBackColorNames["LongQty"] = "long-qty";
    PositionInfoBackColorNames["ShortQty"] = "short-qty";
})(PositionInfoBackColorNames || (PositionInfoBackColorNames = {}));
export class PositionInfoItem {
    constructor(type, calculator) {
        this.Type = type;
        this.positionInfo = calculator;
    }
    Update() {
        const newValue = this.getValue();
        this.UpdateLabel(newValue);
        this.UpdateBackColor(newValue);
    }
    getValue() {
        switch (this.Type) {
            case PositionInfoType.PLByCurrency:
                return this.positionInfo.pLByCurrency;
            case PositionInfoType.AverageOpenPrice:
                return this.positionInfo.averageOpenPrice;
            case PositionInfoType.BreakEven:
                return this.positionInfo.breakEven;
            case PositionInfoType.NetQTY:
                return this.positionInfo.netQty;
            case PositionInfoType.LongQty:
                return this.positionInfo.longQTY;
            case PositionInfoType.ShortQty:
                return this.positionInfo.shortQTY;
            default:
                return 0;
        }
    }
    UpdateLabel(val) {
        if (!isValidNumber(val)) {
            if (isValidNumber(this.positionInfo.netQty) && this.Type !== PositionInfoType.BreakEven) {
                this.Label = PositionInfoItem.NA;
            }
            else {
                this.Label = PositionInfoItem.EMPTY_STR;
            }
            return;
        }
        if (isNullOrUndefined(this.positionInfo.instrument) || isNullOrUndefined(this.positionInfo.account)) {
            this.Label = val.toString();
            return;
        }
        switch (this.Type) {
            case PositionInfoType.PLByCurrency:
                this.Label = this.positionInfo.account.formatPrice(val);
                break;
            case PositionInfoType.AverageOpenPrice:
                this.Label = this.positionInfo.instrument.formatPricePrecision(val);
                break;
            case PositionInfoType.BreakEven:
                if (val >= 0) {
                    this.Label = this.positionInfo.instrument.formatPricePrecision(val);
                }
                else {
                    this.Label = PositionInfoItem.EMPTY_STR;
                }
                break;
            case PositionInfoType.LongQty:
            case PositionInfoType.ShortQty:
            case PositionInfoType.NetQTY: {
                let valueStr;
                if (val === 0) {
                    valueStr = PositionInfoItem.EMPTY_STR;
                }
                else {
                    const ShowLots = BaseSettingsUtils.displayAmountInLots();
                    val = ShowLots ? val : val * this.positionInfo.instrument.LotSize;
                    valueStr = DataCache.formatVolume(this.positionInfo.instrument, val, ShowLots);
                }
                this.Label = valueStr;
                break;
            }
            default:
                this.Label = PositionInfoItem.EMPTY_STR;
        }
    }
    UpdateBackColor(value) {
        const colorNames = PositionInfoBackColorNames;
        switch (this.Type) {
            case PositionInfoType.PLByCurrency:
                if (value > 0) {
                    this.BackColorClass = colorNames.PLByCurrencyPositive;
                }
                else if (value < 0) {
                    this.BackColorClass = colorNames.PLByCurrencyNegative;
                }
                else {
                    this.BackColorClass = colorNames.PLByCurrencyNone;
                }
                break;
            case PositionInfoType.BreakEven:
                if (this.positionInfo.longQTY > 0 && this.positionInfo.shortQTY > 0) {
                    this.BackColorClass = colorNames.BreakEvenLongShortBoth;
                }
                else if (this.positionInfo.longQTY === 0 && this.positionInfo.shortQTY > 0) {
                    this.BackColorClass = colorNames.BreakEvenShortPositive;
                }
                else if (this.positionInfo.longQTY > 0 && this.positionInfo.shortQTY === 0) {
                    this.BackColorClass = colorNames.BreakEvenLongPositive;
                }
                else {
                    this.BackColorClass = colorNames.BreakEvenNone;
                }
                break;
            case PositionInfoType.NetQTY:
                if (value > 0) {
                    this.BackColorClass = colorNames.NetQTYPositive;
                }
                else if (value < 0) {
                    this.BackColorClass = colorNames.NetQTYNegative;
                }
                else {
                    this.BackColorClass = colorNames.NetQTYNone;
                }
                break;
            case PositionInfoType.AverageOpenPrice:
                if (this.positionInfo.allInLong) {
                    this.BackColorClass = colorNames.AverageOpenPriceLong;
                }
                else if (this.positionInfo.allInShort) {
                    this.BackColorClass = colorNames.AverageOpenPriceShort;
                }
                else {
                    this.BackColorClass = colorNames.AverageOpenPriceNone;
                }
                break;
            case PositionInfoType.LongQty:
                this.BackColorClass = colorNames.LongQty;
                break;
            case PositionInfoType.ShortQty:
                this.BackColorClass = colorNames.ShortQty;
                break;
            default:
                this.BackColorClass = colorNames.None;
                break;
        }
    }
}
PositionInfoItem.NA = StringUtils.NOT_AVAILABLE;
PositionInfoItem.EMPTY_STR = StringUtils.THREE_DASHES;
